import { Button, Typo } from "@pepperstone/pepper-ui-kit";
import { useState } from "react";

import { InfoIcon } from "../Icons";
import { ModalContent } from "../ModalContent";
import { TooltipWrapper } from "../Tooltip";

type ResponsiveInfoTooltipProp = {
  title?: string;
  tooltipText: string;
  isMobile?: boolean;
  onHover?: (isMobile: boolean) => void;
  iconType?: "default" | "overview";
  iconSize?: number;
};

/**
 * responsive info tooltip component that displays a tooltip on desktop and a modal on mobile
 * @param title - The title of the tooltip/modal
 * @param tooltipText - The text to display in the tooltip/modal
 */
export const ResponsiveInfoTooltip = ({
  title,
  tooltipText,
  isMobile = false,
  iconType = "default",
  iconSize = 20,
  onHover,
}: ResponsiveInfoTooltipProp) => {
  const [mobileModalOverviewVisible, setMobileModalOverviewVisible] =
    useState(false);

  if (isMobile) {
    return (
      <div
        className="flex flex-row items-center justify-start gap-2 md:justify-end"
        data-testid="responsive-info-tooltip"
      >
        <MobileResponsiveInfoModal
          isVisible={mobileModalOverviewVisible}
          close={() => setMobileModalOverviewVisible(false)}
          title={title}
          statusDescription={tooltipText}
        />
        <span
          data-testid="responsive-info-tooltip-icon"
          className="cursor-pointer"
          onClick={() => {
            onHover?.(true);
            setMobileModalOverviewVisible(true);
          }}
        >
          <ResponsiveInfoTooltipIcon iconSize={iconSize} iconType={iconType} />
        </span>
      </div>
    );
  }

  return (
    <TooltipWrapper
      color="blue"
      tooltipPlacement="top"
      text={tooltipText}
      tooltipTextAlign="text-center"
      trackTooltipHover={() => {
        onHover?.(false);
      }}
    >
      <ResponsiveInfoTooltipIcon iconSize={iconSize} iconType={iconType} />
    </TooltipWrapper>
  );
};

/**
 * NOTE: based on the icon the info icon is generally smaller than the intended iconSize
 * as we are using pepper-ui-kit
 * ie 20 -> 16 icon size
 * @param param0
 * @returns
 */
export const ResponsiveInfoTooltipIcon = ({
  iconType = "default",
  iconSize = 20,
}: {
  iconType?: "default" | "overview";
  iconSize?: number;
  iconColor?: string;
}) => {
  if (iconType == "overview") {
    return (
      <InfoIcon
        className="[&_path]:stroke-[#03142eb3]"
        width={iconSize}
        height={iconSize}
      />
    );
  } else {
    return (
      <InfoIcon
        className="[&_path]:stroke-icon-base"
        width={iconSize}
        height={iconSize}
      />
    );
  }
};

export const MobileResponsiveInfoModal = ({
  isVisible,
  close,
  title,
  statusDescription,
}: {
  isVisible: boolean;
  close: () => void;
  title?: string;
  statusDescription: string;
}) => {
  return (
    <ModalContent isVisible={isVisible} close={close}>
      <div className="flex flex-grow flex-col justify-between gap-4">
        {title && (
          <Typo elem="h1" size="xs" type="heading">
            {title}
          </Typo>
        )}
        <Typo
          elem="p"
          size="xs"
          type="label"
          textColor="ps-text-text-primary-secondary"
        >
          {statusDescription}
        </Typo>
        <Button
          data-testid="ok-button"
          as="a"
          variant="ps-btn-primary"
          size="ps-btn-md"
          onClick={close}
        >
          OK
        </Button>
      </div>
    </ModalContent>
  );
};
